import { Component, OnInit } from '@angular/core';
import { Calendar, Copy, Edit, Eye, Info, LucideAngularModule, Plus, Trash } from 'lucide-angular';
import { RouteParams } from '@app/shared/abstract/route-params';
import { ProductService } from '@app/products/shared/product.service';
import { ProductModel } from '../shared/product.model';
import { Router, RouterLink } from '@angular/router';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { FormControl, FormsModule } from '@angular/forms';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { Destroyer } from '@app/shared/abstract/destroyer';
import { catchError, debounceTime, of, switchMap, takeUntil, takeWhile, timer } from 'rxjs';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { Column } from '@app/partials/datatable/data-table/column';
import { TextInputComponent } from '@app/partials/form/text-input/text-input.component';
import { ModalComponent } from '@app/partials/modal/modal.component';
import { RowAction } from '@app/partials/datatable/data-table/row-action';
import { AuthService } from '@app/auth/auth.service';
import { ToastService } from '@app/shared/toast/toast.service';

type UsageDetails = {
  events: Array<{ id: number; name: string; reference?: string | null; date: string | null }>;
  event_count: number;
};

@Component({
  selector: 'app-index11',
  imports: [
    LucideAngularModule,
    RouterLink,
    DataTableComponent,
    FormsModule,
    SelectComponent,
    TextInputComponent,
    ModalComponent,
  ],
  templateUrl: './products-index.component.html',
  styleUrl: './products-index.component.css',
})
export class ProductsIndexComponent extends Destroyer implements OnInit {
  protected readonly PlusIcon = Plus;
  protected readonly EditIcon = Edit;
  protected readonly TrashIcon = Trash;
  protected readonly CalendarIcon = Calendar;
  protected readonly EyeIcon = Eye;
  protected readonly CopyIcon = Copy;
  protected readonly InfoIcon = Info;

  protected productsPage?: PagedEntities<ProductModel>;
  protected categoriesControl = new FormControl();
  protected isLoading = true;
  protected isAdmin = false;

  protected productToDelete?: ProductModel;
  protected productDeleteError?: string;
  protected productToDuplicate?: ProductModel;
  protected duplicateProcessing = false;
  protected duplicateStatus?: 'queued' | 'processing' | 'finished' | 'failed' | 'cancelled';
  protected productUsageDetails?: UsageDetails;
  protected productUsageProduct?: ProductModel;
  protected productUsageContext: 'delete' | 'view' = 'delete';

  protected categories = [
    { value: 'Venue Hire', label: 'Venue Hire' },
    { value: 'Accommodation', label: 'Accommodation' },
    { value: 'Wedding Morning Ideas – Food', label: 'Wedding Morning Ideas – Food' },
    { value: 'Wedding Morning Ideas – Drink', label: 'Wedding Morning Ideas – Drink' },
    { value: 'Canapes', label: 'Canapes' },
    { value: 'Reception Drinks', label: 'Reception Drinks' },
    { value: 'Wedding Breakfast Menus', label: 'Wedding Breakfast Menus' },
    { value: 'Drinks Packages', label: 'Drinks Packages' },
    { value: 'Wine List', label: 'Wine List' },
    { value: 'Evening Food', label: 'Evening Food' },
    { value: 'Evening Food – Complimentary', label: 'Evening Food – Complimentary' },
    { value: 'Complimentary Items', label: 'Complimentary Items' },
    { value: 'Extras – Food', label: 'Extras – Food' },
    { value: 'Extras – Drink', label: 'Extras – Drink' },
    { value: 'DJ & Entertainment', label: 'DJ & Entertainment' },
    { value: 'Hire Items', label: 'Hire Items' },
    { value: 'Décor', label: 'Décor' },
    { value: 'Floristry', label: 'Floristry' },
    { value: 'Miscellaneous', label: 'Miscellaneous' },
    { value: 'Supplier Meals', label: 'Supplier Meals' },
    { value: 'Security Retainer', label: 'Security Retainer' },
  ];

  routeParams = new RouteParams({
    pageNum: 1,
    pageSize: 100,
  });

  tableColumns: Column<ProductModel>[] = [
    { field: 'id', header: 'Id' },
    { field: 'image_1_sm', header: 'Picture', type: 'image' },
    { field: 'name', header: 'Name', sortable: true },
    { field: 'category', header: 'Category' },
    { field: 'price_formatted', header: 'Price', sortable: true, sortSource: 'price', type: 'number' },
    // { field: 'in_stock', header: 'In Stock', type: 'boolean' },
  ];

  rowActions: RowAction<ProductModel>[] = [];

  searchControl = new FormControl();

  constructor(
    private service: ProductService,
    private authService: AuthService,
    private toastService: ToastService,
    private router: Router,
  ) {
    super();
    this.configureRowActions();
  }

  loadData() {
    this.isLoading = true;
    this.service.getPage(this.routeParams).subscribe({
      next: (res) => {
        this.productsPage = res;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  ngOnInit(): void {
    this.authService.getUser().subscribe({
      next: (user) => {
        this.isAdmin = user?.roles?.includes('admin') ?? false;
        this.configureRowActions();
      },
      error: () => {
        this.isAdmin = false;
        this.configureRowActions();
      },
    });

    this.loadData();

    // Set up search control subscription with debouncing and switchMap to cancel previous requests
    this.searchControl.valueChanges
      .pipe(
        debounceTime(500),
        switchMap(() => {
          this.isLoading = true;
          this.updateFilters();
          return this.service.getPage(this.routeParams);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.productsPage = res;
        this.isLoading = false;
      });

    // Set up categories control subscription with switchMap to cancel previous requests
    this.categoriesControl.valueChanges
      .pipe(
        switchMap(() => {
          this.isLoading = true;
          this.updateFilters();
          return this.service.getPage(this.routeParams);
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((res) => {
        this.productsPage = res;
        this.isLoading = false;
      });
  }

  private updateFilters(): void {
    this.routeParams.filter = [];

    // Add search filter if present
    if (this.searchControl.value) {
      this.routeParams.filter.push({ field: 'search', value: this.searchControl.value });
    }

    // Add category filter if present
    if (this.categoriesControl.value && this.categoriesControl.value.length > 0) {
      this.routeParams.filter.push({ field: 'category', value: this.categoriesControl.value.join(',') });
    }
  }

  deleteProductConfirmed() {
    if (!this.productToDelete) {
      return;
    }
    const deletingProduct = this.productToDelete;

    this.service.delete(this.productToDelete.id + '').subscribe({
      next: () => {
        this.productToDelete = undefined;
        this.loadData();
      },
      error: (err) => {
        if (err.status === 400 && err.error?.events) {
          this.productUsageDetails = {
            events: err.error.events,
            event_count: err.error.event_count || err.error.events.length,
          };
          this.productUsageContext = 'delete';
          this.productUsageProduct = deletingProduct;
        } else {
          this.productDeleteError = err.error.message;
        }
        this.productToDelete = undefined;
      },
    });
  }

  openProductUsage(product: ProductModel) {
    if (!product?.id) {
      return;
    }

    this.productUsageContext = 'view';
    this.productUsageProduct = product;

    this.service.getUsage(product.id).subscribe({
      next: (response) => {
        this.productUsageDetails = response;
      },
      error: () => {
        this.toastService.error('Failed to load product usage');
        this.closeProductUsageModal();
      },
    });
  }

  closeProductUsageModal() {
    this.productUsageDetails = undefined;
    this.productUsageProduct = undefined;
    this.productUsageContext = 'delete';
  }

  handleRoute(sorts: RouteParams) {
    this.routeParams.sort = sorts.sort;
    this.routeParams.pageNum = sorts.pageNum;
    this.routeParams.pageSize = sorts.pageSize;
    this.loadData();
  }

  private configureRowActions(): void {
    if (this.isAdmin) {
      this.rowActions = [
        {
          icon: this.EyeIcon,
          routerLink: (item: ProductModel) => ['/products', item.id, 'view'],
          classes: 'text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500',
        },
        {
          icon: this.CopyIcon,
          classes: 'text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500',
          callback: (product: ProductModel) => {
            this.startDuplicate(product);
          },
          title: () => 'Duplicate product',
        },
        {
          icon: this.EditIcon,
          routerLink: (item: ProductModel) => ['/products', item.id, 'edit'],
        },
        {
          icon: this.CalendarIcon,
          routerLink: (item: ProductModel) => ['/products', item.id, 'prices', 'calendar'],
          classes: 'text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500',
        },
        {
          icon: this.InfoIcon,
          classes: 'text-gray-500 hover:text-primary-500 dark:text-gray-400 dark:hover:text-primary-500',
          callback: (product: ProductModel) => {
            this.openProductUsage(product);
          },
          title: () => 'View usage',
        },
        {
          icon: this.TrashIcon,
          classes: 'text-gray-500 hover:text-error-500 dark:text-gray-400 dark:hover:text-error-500',
          callback: (product: ProductModel) => {
            this.productToDelete = product;
          },
        },
      ];
    } else {
      this.rowActions = [
        {
          icon: this.EyeIcon,
          routerLink: (item: ProductModel) => ['/products', item.id, 'view'],
        },
      ];
    }
  }

  startDuplicate(product: ProductModel): void {
    this.productToDuplicate = product;
  }

  confirmDuplicate(): void {
    if (!this.productToDuplicate?.id) {
      return;
    }

    const productId = this.productToDuplicate.id;
    this.duplicateProcessing = true;
    this.duplicateStatus = 'queued';
    this.service.duplicate(productId).subscribe({
      next: (res) => {
        if (res?.status === 'queued' && res.batch_id) {
          this.toastService.success('Product duplication queued');
          this.duplicateStatus = 'queued';
          this.pollDuplicateStatus(res.batch_id);
          return;
        }

        if (res?.data?.id) {
          this.toastService.success('Product duplicated');
          this.duplicateProcessing = false;
          this.productToDuplicate = undefined;
          this.duplicateStatus = undefined;
          this.router.navigate(['/products', res.data.id, 'edit']);
          return;
        }

        this.toastService.success('Product duplicated');
        this.duplicateProcessing = false;
        this.productToDuplicate = undefined;
        this.duplicateStatus = undefined;
        this.loadData();
      },
      error: (error) => {
        if (error?.status === 409 && error?.error?.batch_id) {
          this.toastService.info('Duplication already in progress');
          this.duplicateStatus = 'processing';
          this.pollDuplicateStatus(error.error.batch_id);
          return;
        }

        this.toastService.error('Failed to duplicate product');
        this.duplicateProcessing = false;
        this.productToDuplicate = undefined;
        this.duplicateStatus = undefined;
      },
    });
  }

  cancelDuplicate(): void {
    if (this.duplicateProcessing) {
      return;
    }
    this.productToDuplicate = undefined;
    this.duplicateStatus = undefined;
  }

  protected duplicateNamePreview(): string {
    if (!this.productToDuplicate?.name) {
      return 'Copy';
    }
    const preview = `${this.productToDuplicate.name} (Copy)`;
    return preview.length > 255 ? preview.slice(0, 255) : preview;
  }

  private pollDuplicateStatus(batchId: string): void {
    timer(0, 2000)
      .pipe(
        switchMap(() => this.service.getDuplicateStatus(batchId)),
        takeWhile(
          (response) => response.status === 'queued' || response.status === 'processing',
          true,
        ),
        catchError(() => {
          this.toastService.error('Failed to check duplication status');
          this.duplicateProcessing = false;
          this.productToDuplicate = undefined;
          return of(null);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((response) => {
        if (!response) {
          return;
        }

        if (response.status === 'finished') {
          this.toastService.success('Product duplicated');
          this.duplicateProcessing = false;
          this.productToDuplicate = undefined;
          this.duplicateStatus = response.status;
          if (response.product_id) {
            this.router.navigate(['/products', response.product_id, 'edit']);
          } else {
            this.loadData();
          }
          return;
        }

        if (response.status === 'failed' || response.status === 'cancelled') {
          this.toastService.error('Product duplication failed');
          this.duplicateProcessing = false;
          this.productToDuplicate = undefined;
          this.duplicateStatus = response.status;
          return;
        }

        this.duplicateStatus = response.status as typeof this.duplicateStatus;
      });
  }

  protected duplicateStatusLabel(): string {
    switch (this.duplicateStatus) {
      case 'queued':
        return 'Queued';
      case 'processing':
        return 'Processing';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      case 'finished':
        return 'Finished';
      default:
        return 'Starting';
    }
  }
}
